/**
 * MANAGER FUNNEL TABLE - Spreadsheet-style operational view
 * =========================================================
 * Google Sheets-like interface for scanning, sorting, filtering
 * and comparing manager performance metrics.
 * 
 * Features:
 * - Column grouping with toggle visibility
 * - Frozen City/Manager columns
 * - Sortable columns
 * - Expandable manager rows for rep drilldown
 */

import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Checkbox } from './ui/checkbox';
import {
  ChevronDown,
  ChevronUp,
  ChevronRight,
  RefreshCw,
  Users,
  ArrowUpDown,
  Filter,
  Loader2,
  Settings2,
  Eye,
  Info
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { toast } from 'sonner';
import { api } from '../utils/api';

// ==================== COLUMN CONFIGURATION ====================

// Column groups definition
const COLUMN_GROUPS = {
  visit: {
    id: 'visit',
    label: 'Visit Metrics',
    columns: ['first_site_visits', 'revisits_done', 'revisit_pct']
  },
  booking: {
    id: 'booking',
    label: 'Booking Metrics',
    columns: ['gross_bookings', 'cancelled', 'net_bookings', 'net_sales_value']
  },
  conversion: {
    id: 'conversion',
    label: 'Conversion Metrics',
    columns: ['cohort_sv_to_booking_pct', 'rolling_sv_to_booking_pct', 'cancellation_pct']
  }
};

// All column definitions with metadata
const COLUMNS = {
  // Basic columns (always visible)
  city: { id: 'city', label: 'City', group: 'basic', sortable: false, align: 'left', frozen: true },
  manager: { id: 'manager', label: 'Manager', group: 'basic', sortable: false, align: 'left', frozen: true },
  rep_count: { id: 'rep_count', label: 'Reps', group: 'basic', sortable: true, align: 'right' },
  
  // Visit Metrics
  first_site_visits: { 
    id: 'first_site_visits', 
    label: 'First SV', 
    group: 'visit', 
    sortable: true, 
    align: 'right',
    colorClass: 'bg-blue-50',
    tooltip: 'First Site Visits - Unique customers whose first project site visit occurred in this period'
  },
  revisits_done: { 
    id: 'revisits_done', 
    label: 'Revisits', 
    group: 'visit', 
    sortable: true, 
    align: 'right',
    tooltip: 'Revisits Done - Unique customers who had a revisit during this period'
  },
  revisit_pct: { 
    id: 'revisit_pct', 
    label: 'Revisit %', 
    group: 'visit', 
    sortable: true, 
    align: 'right',
    colorClass: 'bg-purple-50',
    isPercent: true,
    tooltip: 'Revisit % = Revisits Done / First Site Visits × 100'
  },
  
  // Booking Metrics
  gross_bookings: { 
    id: 'gross_bookings', 
    label: 'Gross', 
    group: 'booking', 
    sortable: true, 
    align: 'right',
    tooltip: 'Gross Bookings - Bookings that reached Strategy Verified stage in this period'
  },
  cancelled: { 
    id: 'cancelled', 
    label: 'Cancelled', 
    group: 'booking', 
    sortable: true, 
    align: 'right',
    colorClass: 'bg-red-50',
    tooltip: 'Cancelled - Bookings from this cohort that later got cancelled'
  },
  net_bookings: { 
    id: 'net_bookings', 
    label: 'Net', 
    group: 'booking', 
    sortable: true, 
    align: 'right',
    colorClass: 'bg-green-50',
    tooltip: 'Net Bookings = Gross Bookings - Cancelled'
  },
  net_sales_value: { 
    id: 'net_sales_value', 
    label: 'Net Sales', 
    group: 'booking', 
    sortable: true, 
    align: 'right',
    colorClass: 'bg-emerald-100 font-bold',
    isCurrency: true,
    tooltip: 'Net Sales Value - Sum of Agreement Value for Net Bookings'
  },
  
  // Conversion Metrics
  cohort_sv_to_booking_pct: { 
    id: 'cohort_sv_to_booking_pct', 
    label: 'Cohort %', 
    group: 'conversion', 
    sortable: true, 
    align: 'right',
    colorClass: 'bg-blue-50',
    isPercent: true,
    isConversion: true,
    tooltip: 'Cohort SV to Booking % - Conversion of customers whose first visit occurred in this period'
  },
  rolling_sv_to_booking_pct: { 
    id: 'rolling_sv_to_booking_pct', 
    label: 'Rolling %', 
    group: 'conversion', 
    sortable: true, 
    align: 'right',
    colorClass: 'bg-teal-50',
    isPercent: true,
    isConversion: true,
    tooltip: 'Rolling SV to Booking % - Bookings in period / Rolling site visits × 100'
  },
  cancellation_pct: { 
    id: 'cancellation_pct', 
    label: 'Cancel %', 
    group: 'conversion', 
    sortable: true, 
    align: 'right',
    colorClass: 'bg-red-50',
    isPercent: true,
    isCancellation: true,
    tooltip: 'Cancellation % = Cancelled / Gross Bookings × 100'
  }
};

// Default visible columns (clean operational view)
const DEFAULT_VISIBLE_COLUMNS = [
  'city', 'manager', 'rep_count',
  'first_site_visits',
  'net_bookings', 'net_sales_value'
];

// ==================== UTILITY FUNCTIONS ====================

// Format number with Indian currency format
const formatCurrency = (value) => {
  if (value === undefined || value === null) return '₹0L';
  return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}L`;
};

// Performance color thresholds for conversion metrics
const getPerformanceColor = (value, col) => {
  if (col.isCancellation) {
    if (value <= 5) return 'text-green-600';
    if (value <= 15) return 'text-amber-600';
    return 'text-red-600';
  }
  if (col.isConversion) {
    if (value >= 15) return 'text-green-600';
    if (value >= 8) return 'text-amber-600';
    return 'text-red-600';
  }
  return '';
};

// Metric color classes
const METRIC_COLORS = {
  first_site_visits: 'bg-blue-50 text-blue-700',
  revisits_done: 'bg-purple-50 text-purple-700',
  gross_bookings: 'bg-slate-100 text-slate-700',
  cancelled: 'bg-red-50 text-red-700',
  net_bookings: 'bg-green-50 text-green-700',
  net_sales_value: 'bg-emerald-100 text-emerald-800 font-bold',
  revisit_pct: 'bg-purple-50 text-purple-700',
  cohort_sv_to_booking_pct: 'bg-blue-50 text-blue-700',
  rolling_sv_to_booking_pct: 'bg-teal-50 text-teal-700',
  cancellation_pct: 'bg-red-50 text-red-700'
};

// ==================== SUB-COMPONENTS ====================

// Sortable Header Cell Component
const SortableHeader = ({ column, sortBy, sortOrder, onSort, isVisible }) => {
  if (!isVisible) return null;
  
  const col = COLUMNS[column];
  const isActive = sortBy === column;
  
  if (!col.sortable) {
    return (
      <th 
        className={`px-3 py-3 font-semibold whitespace-nowrap ${
          col.align === 'left' ? 'text-left' : 'text-right'
        } ${col.frozen ? 'sticky bg-slate-100 z-20' : ''} ${
          col.frozen && column === 'city' ? 'left-0' : ''
        } ${col.frozen && column === 'manager' ? 'left-[100px] border-r border-slate-300' : ''} ${
          col.colorClass || ''
        }`}
        style={col.frozen && column === 'city' ? { minWidth: '100px' } : col.frozen && column === 'manager' ? { minWidth: '160px' } : {}}
      >
        {col.label}
      </th>
    );
  }
  
  return (
    <th 
      className={`px-3 py-3 font-semibold cursor-pointer hover:bg-slate-200 transition-colors select-none whitespace-nowrap ${
        col.align === 'left' ? 'text-left' : 'text-right'
      } ${col.colorClass || ''}`}
      onClick={() => onSort(column)}
      title={col.tooltip}
    >
      <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
        <span>{col.label}</span>
        {isActive ? (
          sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        ) : (
          <ArrowUpDown size={12} className="text-slate-400" />
        )}
      </div>
    </th>
  );
};

// Summary Row Component - Enhanced styling for better visibility
const SummaryRow = ({ totals, visibleColumns }) => {
  const getCellValue = (colId) => {
    switch(colId) {
      case 'city': return 'TOTAL';
      case 'manager': return '—';
      case 'rep_count': return <span className="font-bold">{totals.total_reps} reps</span>;
      case 'first_site_visits': return <span className="font-bold">{totals.first_site_visits?.toLocaleString()}</span>;
      case 'revisits_done': return <span className="font-bold">{totals.revisits_done?.toLocaleString()}</span>;
      case 'gross_bookings': return <span className="font-bold">{totals.gross_bookings?.toLocaleString()}</span>;
      case 'cancelled': return <span className="font-bold">{totals.cancelled?.toLocaleString()}</span>;
      case 'net_bookings': return <span className="font-bold text-lg">{totals.net_bookings?.toLocaleString()}</span>;
      case 'net_sales_value': return <span className="font-bold text-lg text-emerald-300">{formatCurrency(totals.net_sales_value)}</span>;
      case 'revisit_pct': return <span className="font-bold">{totals.avg_revisit_pct}%</span>;
      case 'cohort_sv_to_booking_pct': return <span className="font-bold">{totals.avg_cohort_sv_to_booking_pct}%</span>;
      case 'rolling_sv_to_booking_pct': return <span className="font-bold">{totals.avg_rolling_sv_to_booking_pct}%</span>;
      case 'cancellation_pct': return <span className="font-bold">{totals.avg_cancellation_pct}%</span>;
      default: return '—';
    }
  };
  
  return (
    <tr className="bg-slate-900 text-white font-semibold sticky top-0 z-20 text-[13px]">
      {Object.keys(COLUMNS).map(colId => {
        if (!visibleColumns.includes(colId)) return null;
        const col = COLUMNS[colId];
        return (
          <td 
            key={colId}
            className={`px-3 py-3.5 ${col.align === 'left' ? 'text-left' : 'text-right'} ${
              col.frozen ? 'sticky bg-slate-900 z-30' : ''
            } ${col.frozen && colId === 'city' ? 'left-0' : ''} ${
              col.frozen && colId === 'manager' ? 'left-[100px]' : ''
            }`}
          >
            {getCellValue(colId)}
          </td>
        );
      })}
    </tr>
  );
};

// Rep Drilldown Row Component
const RepDrilldownRow = ({ rep, visibleColumns }) => {
  const getCellValue = (colId) => {
    switch(colId) {
      case 'city': return <><span className="text-slate-600">↳</span> {rep.rep_name}</>;
      case 'manager': return '-';
      case 'rep_count': return <span className="text-slate-400">-</span>;
      case 'first_site_visits': return rep.first_site_visits;
      case 'revisits_done': return rep.revisits_done;
      case 'gross_bookings': return rep.gross_bookings;
      case 'cancelled': return rep.cancelled;
      case 'net_bookings': return <span className="font-medium">{rep.net_bookings}</span>;
      case 'net_sales_value': return <span className="font-medium text-emerald-700">{formatCurrency(rep.net_sales_value)}</span>;
      case 'revisit_pct':
      case 'cohort_sv_to_booking_pct':
      case 'rolling_sv_to_booking_pct':
      case 'cancellation_pct':
        return <span className="text-slate-400">-</span>;
      default: return '-';
    }
  };
  
  return (
    <tr className="bg-slate-50 border-b border-slate-200 text-sm">
      {Object.keys(COLUMNS).map(colId => {
        if (!visibleColumns.includes(colId)) return null;
        const col = COLUMNS[colId];
        return (
          <td 
            key={colId}
            className={`px-3 py-2 ${colId === 'city' ? 'pl-8' : ''} ${
              col.align === 'left' ? 'text-left' : 'text-right'
            } ${col.frozen ? 'sticky bg-slate-50 z-10' : ''} ${
              col.frozen && colId === 'city' ? 'left-0' : ''
            } ${col.frozen && colId === 'manager' ? 'left-[100px]' : ''}`}
          >
            {getCellValue(colId)}
          </td>
        );
      })}
    </tr>
  );
};

// Manager Data Row Component
const ManagerRow = ({ manager, isExpanded, onToggle, visibleColumns, idx }) => {
  const getCellValue = (colId) => {
    const col = COLUMNS[colId];
    const value = manager[colId];
    
    switch(colId) {
      case 'city':
        return <span className="text-slate-600 text-xs">{manager.city || '-'}</span>;
      case 'manager':
        return (
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown size={16} className="text-blue-600" />
            ) : (
              <ChevronRight size={16} className="text-slate-400" />
            )}
            <span className="font-medium text-slate-800">{manager.manager_name}</span>
          </div>
        );
      case 'rep_count':
        return <span className="text-slate-600">{manager.rep_count}</span>;
      case 'net_sales_value':
        return formatCurrency(value);
      default:
        if (col.isPercent) {
          const colorClass = getPerformanceColor(value, col);
          return <span className={colorClass}>{value}%</span>;
        }
        return value;
    }
  };
  
  const getMetricColorClass = (colId) => {
    return METRIC_COLORS[colId] || '';
  };
  
  return (
    <tr 
      className={`border-b border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors ${
        isExpanded ? 'bg-blue-50' : ''
      } ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
      onClick={onToggle}
      data-testid={`manager-row-${manager.manager_id}`}
    >
      {Object.keys(COLUMNS).map(colId => {
        if (!visibleColumns.includes(colId)) return null;
        const col = COLUMNS[colId];
        const metricClass = ['first_site_visits', 'revisits_done', 'gross_bookings', 'cancelled', 'net_bookings', 'net_sales_value'].includes(colId) 
          ? getMetricColorClass(colId) 
          : '';
        
        return (
          <td 
            key={colId}
            className={`px-3 py-3 ${col.align === 'left' ? 'text-left' : 'text-right'} ${
              col.frozen ? 'sticky bg-inherit z-10' : ''
            } ${col.frozen && colId === 'city' ? 'left-0' : ''} ${
              col.frozen && colId === 'manager' ? 'left-[100px] border-r border-slate-200' : ''
            } ${metricClass}`}
          >
            {getCellValue(colId)}
          </td>
        );
      })}
    </tr>
  );
};

// Column Control Dropdown Component
const ColumnControl = ({ visibleGroups, onToggleGroup }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="columns-control">
          <Settings2 size={14} className="mr-1" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56" align="end">
        <div className="space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b">
            <Eye size={14} className="text-slate-500" />
            <span className="text-sm font-medium">Show Column Groups</span>
          </div>
          {Object.values(COLUMN_GROUPS).map(group => (
            <div key={group.id} className="flex items-center space-x-2">
              <Checkbox 
                id={`group-${group.id}`}
                checked={visibleGroups[group.id]}
                onCheckedChange={() => onToggleGroup(group.id)}
                data-testid={`toggle-${group.id}`}
              />
              <label 
                htmlFor={`group-${group.id}`}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                {group.label}
              </label>
            </div>
          ))}
          <div className="pt-2 border-t">
            <p className="text-xs text-slate-500">
              City, Manager, Reps are always visible
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Metric Definitions Tooltip Component
const MetricDefinitionsTooltip = ({ visibleColumns }) => {
  const definitions = [
    { id: 'first_site_visits', label: 'First SV', desc: 'Unique customers whose first site visit occurred in this period' },
    { id: 'revisits_done', label: 'Revisits', desc: 'Unique customers who had a revisit during this period' },
    { id: 'revisit_pct', label: 'Revisit %', desc: 'Revisits Done / First Site Visits × 100' },
    { id: 'gross_bookings', label: 'Gross', desc: 'Bookings that reached Strategy Verified stage in this period' },
    { id: 'cancelled', label: 'Cancelled', desc: 'Bookings from this cohort that later got cancelled' },
    { id: 'net_bookings', label: 'Net', desc: 'Gross Bookings − Cancelled' },
    { id: 'net_sales_value', label: 'Net Sales', desc: 'Sum of Agreement Value for Net Bookings' },
    { id: 'cohort_sv_to_booking_pct', label: 'Cohort %', desc: 'Bookings from customers whose first visit was in this period' },
    { id: 'rolling_sv_to_booking_pct', label: 'Rolling %', desc: 'All bookings / All unique customers visited in period' },
    { id: 'cancellation_pct', label: 'Cancel %', desc: 'Cancelled bookings / Gross bookings from this cohort' },
  ];
  
  const visibleDefs = definitions.filter(d => visibleColumns.includes(d.id));
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <button className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors" data-testid="metric-definitions-tooltip">
            <span>Metric Definitions</span>
            <Info size={14} className="text-slate-400" />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          align="end" 
          className="bg-white border border-slate-200 shadow-lg max-w-sm p-3"
        >
          <div className="space-y-2 text-xs">
            {visibleDefs.map(def => (
              <div key={def.id} className="flex gap-2">
                <span className="font-semibold text-slate-700 min-w-[70px]">{def.label}:</span>
                <span className="text-slate-600">{def.desc}</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ==================== MAIN COMPONENT ====================

const ManagerFunnelTable = ({ 
  defaultPeriod = 'mtd',
  showFilters = true 
}) => {
  // Data state
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(defaultPeriod);
  const [cityFilter, setCityFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [sortBy, setSortBy] = useState('net_sales_value');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedManagers, setExpandedManagers] = useState({});
  const [repData, setRepData] = useState({});
  const [loadingReps, setLoadingReps] = useState({});
  
  // Column visibility state (session only)
  // Default: Basic + First SV + Net Bookings + Net Sales (clean view)
  const [visibleGroups, setVisibleGroups] = useState({
    visit: false,    // Only First SV shown by default
    booking: false,  // Only Net Bookings + Net Sales shown by default
    conversion: false
  });
  
  // Calculate visible columns based on group toggles
  const getVisibleColumns = useCallback(() => {
    const cols = ['city', 'manager', 'rep_count']; // Always visible
    
    // If visit group is enabled, show all visit columns
    // Otherwise only show first_site_visits
    if (visibleGroups.visit) {
      cols.push(...COLUMN_GROUPS.visit.columns);
    } else {
      cols.push('first_site_visits'); // Default visible
    }
    
    // If booking group is enabled, show all booking columns
    // Otherwise only show net_bookings and net_sales_value
    if (visibleGroups.booking) {
      cols.push(...COLUMN_GROUPS.booking.columns);
    } else {
      cols.push('net_bookings', 'net_sales_value'); // Default visible
    }
    
    // If conversion group is enabled, show all conversion columns
    if (visibleGroups.conversion) {
      cols.push(...COLUMN_GROUPS.conversion.columns);
    }
    
    return cols;
  }, [visibleGroups]);
  
  const visibleColumns = getVisibleColumns();

  // Fetch table data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        period, 
        sort_by: sortBy, 
        sort_order: sortOrder 
      };
      if (cityFilter && cityFilter !== 'all') {
        params.city_filter = cityFilter;
      }
      if (managerFilter && managerFilter !== 'all') {
        params.manager_filter = managerFilter;
      }
      
      const response = await api.getManagerFunnelTable(params);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch manager funnel table:', error);
      toast.error('Failed to load manager funnel data');
    } finally {
      setLoading(false);
    }
  }, [period, cityFilter, managerFilter, sortBy, sortOrder]);

  // Fetch rep drilldown
  const fetchRepDrilldown = useCallback(async (managerId) => {
    if (repData[managerId]) return;
    
    setLoadingReps(prev => ({ ...prev, [managerId]: true }));
    try {
      const response = await api.getManagerFunnelTableReps(managerId, { period });
      setRepData(prev => ({ ...prev, [managerId]: response.data.reps }));
    } catch (error) {
      console.error('Failed to fetch rep drilldown:', error);
      toast.error('Failed to load rep data');
    } finally {
      setLoadingReps(prev => ({ ...prev, [managerId]: false }));
    }
  }, [period, repData]);

  // Toggle manager expansion
  const toggleManager = (managerId) => {
    setExpandedManagers(prev => {
      const isExpanding = !prev[managerId];
      if (isExpanding) {
        fetchRepDrilldown(managerId);
      }
      return { ...prev, [managerId]: isExpanding };
    });
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  
  // Toggle column group visibility
  const toggleGroup = (groupId) => {
    setVisibleGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset expanded managers when filters change
  useEffect(() => {
    setExpandedManagers({});
    setRepData({});
  }, [period, cityFilter, managerFilter]);

  // Count visible columns for colSpan
  const visibleColCount = visibleColumns.length;

  if (loading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
          <span className="text-slate-600">Loading manager funnel data...</span>
        </div>
      </Card>
    );
  }

  if (!data || !data.manager_rows) {
    return (
      <Card className="p-8 text-center">
        <Users size={48} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500">No manager data available</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="manager-funnel-table">
      {/* Filters Bar */}
      {showFilters && (
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* === FILTERS GROUP === */}
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-slate-500" />
              <Select value={cityFilter} onValueChange={setCityFilter}>
                <SelectTrigger className="w-[150px]" data-testid="city-filter">
                  <SelectValue placeholder="City" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {data.available_cities?.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Manager Filter */}
            <Select value={managerFilter} onValueChange={setManagerFilter}>
              <SelectTrigger className="w-[180px]" data-testid="manager-filter">
                <SelectValue placeholder="Manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Managers</SelectItem>
                {data.available_managers?.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} {m.city && `(${m.city})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Period Toggle */}
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              {['mtd', 'qtd', 'fytd'].map(p => (
                <Button
                  key={p}
                  variant={period === p ? 'default' : 'ghost'}
                  size="sm"
                  className={`px-3 ${period === p ? 'bg-slate-900 text-white' : ''}`}
                  onClick={() => setPeriod(p)}
                  data-testid={`period-${p}`}
                >
                  {p.toUpperCase()}
                </Button>
              ))}
            </div>

            {/* Separator */}
            <div className="h-6 w-px bg-slate-200" />

            {/* === SORT BY === */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[160px]" data-testid="sort-by">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="net_sales_value">Net Sales Value</SelectItem>
                <SelectItem value="net_bookings">Net Bookings</SelectItem>
                <SelectItem value="first_site_visits">First Site Visits</SelectItem>
                <SelectItem value="cohort_sv_to_booking_pct">Cohort Conversion</SelectItem>
                <SelectItem value="rolling_sv_to_booking_pct">Rolling Conversion</SelectItem>
                <SelectItem value="cancellation_pct">Cancellation %</SelectItem>
              </SelectContent>
            </Select>

            {/* === COLUMNS === */}
            <ColumnControl 
              visibleGroups={visibleGroups} 
              onToggleGroup={toggleGroup} 
            />

            {/* === REFRESH (pushed to right) === */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchData}
              className="ml-auto"
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} className="mr-1" />
              Refresh
            </Button>
          </div>

          {/* Active Filter Info + Metric Definitions Tooltip */}
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Badge variant="outline" className="bg-white">
                {data.effective_city || 'Pan India'}
              </Badge>
              <span>•</span>
              <span>{data.manager_rows.length} managers</span>
              <span>•</span>
              <span>{data.totals?.total_reps || 0} reps</span>
              <span>•</span>
              <span className="font-medium text-emerald-700">
                {formatCurrency(data.totals?.net_sales_value)} Total Net Sales
              </span>
              {/* Show active column groups */}
              {(visibleGroups.visit || visibleGroups.booking || visibleGroups.conversion) && (
                <>
                  <span>•</span>
                  <span className="text-xs">
                    Showing: {[
                      visibleGroups.visit && 'Visit',
                      visibleGroups.booking && 'Booking',
                      visibleGroups.conversion && 'Conversion'
                    ].filter(Boolean).join(', ')} metrics
                  </span>
                </>
              )}
            </div>
            
            {/* Metric Definitions Tooltip - Replaces the Card below */}
            <MetricDefinitionsTooltip visibleColumns={visibleColumns} />
          </div>
        </Card>
      )}

      {/* Main Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="funnel-table">
            <thead>
              <tr className="bg-slate-100 border-b border-slate-300">
                {Object.keys(COLUMNS).map(colId => (
                  <SortableHeader
                    key={colId}
                    column={colId}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    isVisible={visibleColumns.includes(colId)}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Summary Row */}
              <SummaryRow totals={data.totals} visibleColumns={visibleColumns} />

              {/* Manager Rows */}
              {data.manager_rows.map((manager, idx) => (
                <Fragment key={manager.manager_id}>
                  <ManagerRow
                    manager={manager}
                    isExpanded={expandedManagers[manager.manager_id]}
                    onToggle={() => toggleManager(manager.manager_id)}
                    visibleColumns={visibleColumns}
                    idx={idx}
                  />

                  {/* Rep Drilldown Rows */}
                  {expandedManagers[manager.manager_id] && (
                    <>
                      {loadingReps[manager.manager_id] ? (
                        <tr className="bg-slate-50">
                          <td colSpan={visibleColCount} className="px-6 py-4 text-center text-slate-500">
                            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                            Loading reps...
                          </td>
                        </tr>
                      ) : repData[manager.manager_id]?.length > 0 ? (
                        repData[manager.manager_id].map(rep => (
                          <RepDrilldownRow 
                            key={rep.rep_id} 
                            rep={rep} 
                            visibleColumns={visibleColumns}
                          />
                        ))
                      ) : (
                        <tr className="bg-slate-50">
                          <td colSpan={visibleColCount} className="px-6 py-3 text-center text-slate-500 text-sm">
                            No reps mapped to this manager
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default ManagerFunnelTable;
